import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { generatePuzzle, pickStarterReveals } from './engine';
import type { CryptogramLevel, GeneratedPuzzle, PuzzleCell } from './types';
import type { GauntletAware } from '../../shared/gauntletStep';
import './cryptogram.css';

const MAX_MISTAKES = 3;

export default function CryptogramGame() {
  const [data, setData] = useState<{
    level: CryptogramLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<CryptogramLevel>('cryptogram')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Cryptogram yet.');
        } else {
          setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
        }
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="cryptogram" />;
  if (!data) {
    return (
      <GameShell gameId="cryptogram">
        <LoadingScreen label="Fetching today's cipher…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('cryptogram', data.dateKey)) {
    return (
      <FinishedDaily gameId="cryptogram" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <CryptogramPlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

export function CryptogramPlay({
  level,
  dateKey,
  isToday,
  gauntlet,
}: GauntletAware & {
  level: CryptogramLevel;
  dateKey: string;
  isToday: boolean;
}) {
  // Deterministic cipher seeded by dateKey so everyone gets the same cipher for the same day.
  const seed = useMemo(() => {
    let h = 0;
    for (let i = 0; i < dateKey.length; i++) h = (h * 31 + dateKey.charCodeAt(i)) >>> 0;
    return h || 1;
  }, [dateKey]);

  const puzzle = useMemo<GeneratedPuzzle>(() => generatePuzzle(level, seed), [level, seed]);
  const starter = useMemo(() => pickStarterReveals(puzzle), [puzzle]);

  const { restored, save } = useDailySnapshot<{
    guesses: Record<number, string>;
    revealed: number[];
    mistakes: number;
    hintUsed: boolean;
  }>('cryptogram', dateKey);

  const [guesses, setGuesses] = useState<Record<number, string>>(() =>
    restored ? restored.guesses : { ...starter.revealedGuesses },
  );
  const [revealed, setRevealed] = useState<Set<number>>(
    () => new Set(restored ? restored.revealed : starter.revealedNumbers),
  );
  const [mistakes, setMistakes] = useState(restored?.mistakes ?? 0);
  const [gameOver, setGameOver] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(
    save,
    { guesses, revealed: [...revealed], mistakes, hintUsed },
    !gauntlet && !completed && !gameOver,
  );

  const letterCells = useMemo(
    () => puzzle.cells.filter((c) => c.type === 'letter'),
    [puzzle],
  );

  const [selectedNumber, setSelectedNumber] = useState<number | null>(() => {
    const first = letterCells.find(
      (c) => c.number != null && !starter.revealedNumbers.includes(c.number),
    );
    return first?.number ?? null;
  });
  const [selectedPos, setSelectedPos] = useState<number | null>(() => {
    const first = letterCells.find(
      (c) => c.number != null && !starter.revealedNumbers.includes(c.number),
    );
    return first?.index ?? null;
  });

  const allNumbers = useMemo(
    () => Object.keys(puzzle.numberToLetter).map(Number),
    [puzzle],
  );

  // The host may hand a fresh `gauntlet` object each render, so guard the
  // solved callback with a ref rather than relying on effect deps.
  const solvedRef = useRef(false);
  useEffect(() => {
    if (!completed || solvedRef.current) return;
    solvedRef.current = true;
    // In the gauntlet the host owns completion; daily mode records its own.
    if (gauntlet) gauntlet.onSolved();
    else recordOutcome('cryptogram', dateKey, 'won', {
      subtitle: `“${puzzle.text}” — ${puzzle.author}`,
    });
  }, [completed, dateKey, gauntlet]);

  const moveSelection = useCallback(
    (forward: boolean) => {
      if (letterCells.length === 0) return;
      const currentPos =
        selectedPos != null
          ? letterCells.findIndex((c) => c.index === selectedPos)
          : forward
          ? -1
          : letterCells.length;
      const count = letterCells.length;
      for (let offset = 1; offset <= count; offset++) {
        const raw = currentPos + (forward ? offset : -offset);
        const nextIdx = ((raw % count) + count) % count;
        const candidate = letterCells[nextIdx];
        if (candidate.number != null && !revealed.has(candidate.number)) {
          setSelectedNumber(candidate.number);
          setSelectedPos(candidate.index);
          return;
        }
      }
    },
    [letterCells, selectedPos, revealed],
  );

  const selectCell = useCallback(
    (cell: PuzzleCell) => {
      if (cell.number == null || revealed.has(cell.number)) return;
      setSelectedNumber(cell.number);
      setSelectedPos(cell.index);
    },
    [revealed],
  );

  const checkCompletion = useCallback(
    (next: Record<number, string>) => {
      const done = allNumbers.every((n) => next[n] === puzzle.numberToLetter[n]);
      if (done) setCompleted(true);
    },
    [allNumbers, puzzle],
  );

  const assignLetter = useCallback(
    (letter: string) => {
      if (gameOver || completed) return;
      if (selectedNumber == null || revealed.has(selectedNumber)) return;

      const upper = letter.toUpperCase();
      const correct = puzzle.numberToLetter[selectedNumber];

      setGuesses((prev) => {
        const next = { ...prev, [selectedNumber]: upper };
        if (upper === correct) checkCompletion(next);
        return next;
      });

      if (upper === correct) {
        moveSelection(true);
      } else if (gauntlet) {
        // Lives live on the host in gauntlet mode — no local game-over.
        gauntlet.onWrong();
        setMistakes((m) => m + 1);
      } else {
        setMistakes((m) => {
          const nm = m + 1;
          if (nm >= MAX_MISTAKES) {
            setGameOver(true);
            recordOutcome('cryptogram', dateKey, 'lost', {
              subtitle: `Ran out of mistakes.`,
              answer: puzzle.text,
            });
          }
          return nm;
        });
      }
    },
    [
      gameOver,
      completed,
      selectedNumber,
      revealed,
      puzzle,
      moveSelection,
      checkCompletion,
      gauntlet,
      dateKey,
    ],
  );

  const backspace = useCallback(() => {
    if (selectedNumber == null || revealed.has(selectedNumber)) return;
    setGuesses((prev) => {
      const { [selectedNumber]: _removed, ...rest } = prev;
      return rest;
    });
  }, [selectedNumber, revealed]);

  const useHint = useCallback(() => {
    // In the gauntlet the host gates availability against the shared pool, so
    // the one-per-puzzle local limit doesn't apply.
    if (gameOver || completed || (hintUsed && !gauntlet)) return;
    const unsolved = allNumbers.filter(
      (n) => !revealed.has(n) && guesses[n] !== puzzle.numberToLetter[n],
    );
    if (unsolved.length === 0) return;
    const target =
      selectedNumber != null && unsolved.includes(selectedNumber)
        ? selectedNumber
        : unsolved[Math.floor(Math.random() * unsolved.length)];
    setGuesses((prev) => {
      const next = { ...prev, [target]: puzzle.numberToLetter[target] };
      checkCompletion(next);
      return next;
    });
    setRevealed((prev) => new Set(prev).add(target));
    setHintUsed(true);
    if (target === selectedNumber) moveSelection(true);
  }, [
    allNumbers,
    revealed,
    guesses,
    puzzle,
    selectedNumber,
    moveSelection,
    gameOver,
    completed,
    hintUsed,
    checkCompletion,
    gauntlet,
  ]);

  // Each increment of the host's hint signal spends one pooled hint here.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      useHint();
    }
  }, [gauntlet?.hintSignal, useHint]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (/^[a-zA-Z]$/.test(key)) {
        e.preventDefault();
        assignLetter(key);
      } else if (key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
        backspace();
      } else if (key === 'ArrowRight') {
        e.preventDefault();
        moveSelection(true);
      } else if (key === 'ArrowLeft') {
        e.preventDefault();
        moveSelection(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [assignLetter, backspace, moveSelection]);

  const actions = (
    <button
      className="btn btn-secondary"
      onClick={useHint}
      disabled={gameOver || completed || hintUsed}
      title="Reveal a letter (one per puzzle)"
    >
      💡 Hint
    </button>
  );

  const body = (
    <>
      <div className="game-status-row">
        {!gauntlet && (
          <div className="game-status-chip">
            Mistakes <strong>{mistakes}</strong>/{MAX_MISTAKES}
          </div>
        )}
        <div className="game-status-chip">
          Author <strong>“{level.author}”</strong>
        </div>
      </div>

      <div className="crypto-board">
        {puzzle.words.map((word, wi) => (
          <div className="crypto-word" key={wi}>
            {word.map((cell) => {
              if (cell.type === 'punctuation') {
                return (
                  <span key={cell.index} className="crypto-punct">
                    {cell.char}
                  </span>
                );
              }
              const num = cell.number!;
              const guess = guesses[num] ?? '';
              const isRevealed = revealed.has(num);
              const isCorrect = guess && guess === puzzle.numberToLetter[num];
              const isSelected = selectedPos === cell.index;
              const isSameNumber = selectedNumber === num && !isSelected;
              return (
                <button
                  key={cell.index}
                  className={
                    'crypto-cell' +
                    (isSelected ? ' is-selected' : '') +
                    (isSameNumber ? ' is-peer' : '') +
                    (isCorrect ? ' is-correct' : '') +
                    (isRevealed ? ' is-revealed' : '') +
                    (guess && !isCorrect ? ' is-wrong' : '')
                  }
                  onClick={() => selectCell(cell)}
                >
                  <span className="crypto-cell-letter">{guess}</span>
                  <span className="crypto-cell-number">{num}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <OnScreenKeyboard
        onLetter={assignLetter}
        onBack={backspace}
        onPrev={() => moveSelection(false)}
        onNext={() => moveSelection(true)}
        disabled={gameOver || completed || selectedNumber == null}
        navDisabled={gameOver || completed}
        usedLetters={new Set(Object.values(guesses).filter(Boolean))}
        correctLetters={
          new Set(
            Object.entries(guesses)
              .filter(([n, l]) => puzzle.numberToLetter[+n] === l)
              .map(([, l]) => l),
          )
        }
      />

      {completed && !gauntlet && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`“${puzzle.text}” — ${puzzle.author}`}
        />
      )}
      {gameOver && !gauntlet && (
        <GameOverOverlay
          onRetry={() => {
            setMistakes(0);
            setGameOver(false);
            setGuesses({ ...starter.revealedGuesses });
            setRevealed(new Set(starter.revealedNumbers));
            setHintUsed(false);
          }}
        />
      )}
    </>
  );

  // In the gauntlet the host supplies the chrome, so render bare.
  if (gauntlet) return <div className="gauntlet-embed">{body}</div>;

  return (
    <GameShell
      gameId="cryptogram"
      actions={actions}
      subtitle={formatDateLabel(dateKey, isToday)}
    >
      {body}
    </GameShell>
  );
}

function OnScreenKeyboard({
  onLetter,
  onBack,
  onPrev,
  onNext,
  disabled,
  navDisabled,
  usedLetters,
  correctLetters,
}: {
  onLetter: (l: string) => void;
  onBack: () => void;
  onPrev: () => void;
  onNext: () => void;
  disabled: boolean;
  /* Letter keys need a selected cell; the cursor keys are how you get one, so
     they stay live for as long as the puzzle is playable. */
  navDisabled: boolean;
  usedLetters: Set<string>;
  correctLetters: Set<string>;
}) {
  const rows = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
  return (
    <div className="crypto-keyboard" aria-hidden={disabled}>
      {rows.map((row, i) => (
        <div className="crypto-keyboard-row" key={i}>
          {row.split('').map((letter) => (
            <button
              key={letter}
              className={
                'crypto-key' +
                (correctLetters.has(letter)
                  ? ' is-correct'
                  : usedLetters.has(letter)
                  ? ' is-used'
                  : '')
              }
              disabled={disabled}
              onClick={() => onLetter(letter)}
            >
              {letter}
            </button>
          ))}
          {i === rows.length - 1 && (
            <>
              <button
                className="crypto-key crypto-key-nav"
                disabled={navDisabled}
                onClick={onPrev}
                aria-label="Previous letter"
              >
                ‹
              </button>
              <button
                className="crypto-key crypto-key-nav"
                disabled={navDisabled}
                onClick={onNext}
                aria-label="Next letter"
              >
                ›
              </button>
              <button
                className="crypto-key crypto-key-back"
                disabled={disabled}
                onClick={onBack}
                aria-label="Delete"
              >
                ⌫
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function GameOverOverlay({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="crypto-overlay">
      <div className="crypto-overlay-card">
        <div className="crypto-overlay-emoji">😵</div>
        <div className="crypto-overlay-title">Out of tries</div>
        <div className="crypto-overlay-sub">Take another shot at today's cipher.</div>
        <button className="btn btn-primary" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}
